// ============================================================================
// Figures d'une SKILLCARD : version FIGÉE (recto imprimable) et version
// MANIPULABLE (page de la carte).
// ============================================================================
// Sur la carte elle-même (63 × 88 mm, retournable, imprimable), une figure ne
// peut pas se manipuler : un toucher retourne la carte et des curseurs n'y
// tiendraient pas. On y affiche donc la figure figée dans sa position initiale.
// Les figures à contraintes (points déplaçables, curseurs) se manipulent sur la
// page de la carte, sous le recto. Module PUR.
// ============================================================================

import type { CardContent } from './schemas';

type VisualSection = Extract<CardContent['sections'][number], { kind: 'visual' }>;
type VisualBlockLike = VisualSection['block'];

/** Sections « figure » du recto, dans l'ordre. */
export function cardVisualSections(content: CardContent | null | undefined): VisualSection[] {
  return (content?.sections ?? []).filter((s): s is VisualSection => s.kind === 'visual');
}

/**
 * Une figure se manipule si c'est une figure à contraintes interactive :
 * points déplaçables ou curseurs (`variables` / `parameter`).
 */
export function isManipulableCardVisual(block: VisualBlockLike): boolean {
  if (block.type !== 'constrained_geometry') return false;
  const b = block as Record<string, unknown>;
  if (b.interactive === false) return false;
  const points = Array.isArray(b.points) ? (b.points as { fixed?: boolean }[]) : [];
  const hasDraggable = points.some((p) => !p.fixed);
  const hasSlider = (Array.isArray(b.variables) && b.variables.length > 0) || !!b.parameter;
  return hasDraggable || hasSlider;
}

/** Figures manipulables du recto (panneau « Manipuler la figure »). */
export function manipulableCardVisuals(content: CardContent | null | undefined): VisualSection[] {
  return cardVisualSections(content).filter((s) => isManipulableCardVisual(s.block));
}

/** Copie FIGÉE d'une figure pour le recto : ni glisser, ni curseurs. */
export function staticCardVisualBlock(block: VisualBlockLike): VisualBlockLike {
  if (block.type !== 'constrained_geometry') return block;
  const policy = (block as { interactionPolicy?: Record<string, unknown> }).interactionPolicy;
  return {
    ...block,
    interactive: false,
    interactionPolicy: { ...(policy ?? {}), showSlider: false },
  } as VisualBlockLike;
}
